import { memo, useMemo } from "react";
import {
  CartesianGrid,
  ComposedChart,
  Line,
  ReferenceArea,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Key, LIVE_COLORS } from "components/field/RadarSpectrum";
import { LAB_COLORS, finiteNumber, formatCm, levelSeries, rulerPoints } from "utils/radarLab";

const { ruler: RULER_COLOR, spectrum: SPECTRUM_COLOR, jump: JUMP_COLOR } = LAB_COLORS;
// The overflow guards' lines: the tank's ceiling (no fill that could pass it
// is pumped) and the deepest the run may take the water (max_drain_cm below
// its start), beside the tank bounds.
const BOUNDS_COLOR = "#c9792b";
const CEILING_COLOR = "#a8323e";

const clock = (ms) =>
  new Intl.DateTimeFormat("en-SG", {
    timeZone: "Asia/Singapore",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(new Date(ms));

function Tip({ active, payload }) {
  const row = active ? payload?.[0]?.payload : null;
  if (!row) return null;
  return (
    <div className="field-burst-tip">
      <b>{clock(row.ms)} SGT</b>
      {row.ruler !== null && <span>Ruler camera {formatCm(row.ruler, 2)}</span>}
      {row.measured !== null && (
        <span>
          Measured {formatCm(row.measured, 2)}
          {row.jump !== null ? " · jumped" : ""}
        </span>
      )}
      {row.spectrum !== null && <span>Spectrum {formatCm(row.spectrum, 2)}</span>}
      {row.planned !== null && <span>Planned {formatCm(row.planned, 2)}</span>}
    </div>
  );
}

const EMPTY = { measured: null, planned: null, spectrum: null, ruler: null, jump: null };

// The lab run's level over time: what node 1 measured (our estimate of its air
// gap through the run's mapping; the spectrum's alone dotted), what the plan
// expects, the ruler camera's readings of the step photos (the ground truth)
// and the estimates that jumped faster than the pump moves the water. The
// pump's runs are shaded, and the tank bounds, the tank's ceiling and the
// deepest drain allowed are drawn when they are near.
const LabLevelChart = memo(function LabLevelChart({ readings, run, limits }) {
  const { rows, domain, pumping, has } = useMemo(() => {
    const { measured, planned } = levelSeries(readings, run);
    const spectrum = (readings || [])
      .map((p) => ({ ms: Date.parse(p.t), level: finiteNumber(p.spectrum_level_cm) }))
      .filter((p) => Number.isFinite(p.ms) && p.level !== null);
    const jumps = (readings || [])
      .filter((p) => p.jump)
      .map((p) => ({ ms: Date.parse(p.t), level: finiteNumber(p.level_cm) }))
      .filter((p) => Number.isFinite(p.ms) && p.level !== null);
    const ruler = rulerPoints(run);
    const all = [
      ...measured.map((p) => ({ ...EMPTY, ms: p.ms, measured: p.level })),
      ...planned.map((p) => ({ ...EMPTY, ms: p.ms, planned: p.level })),
      ...spectrum.map((p) => ({ ...EMPTY, ms: p.ms, spectrum: p.level })),
      ...jumps.map((p) => ({ ...EMPTY, ms: p.ms, jump: p.level })),
      ...ruler.map((p) => ({ ...EMPTY, ms: p.ms, ruler: p.level })),
    ].sort((a, b) => a.ms - b.ms);
    const values = all
      .flatMap((r) => [r.measured, r.planned, r.spectrum, r.ruler])
      .filter((v) => v !== null);
    const lo = values.length ? Math.floor(Math.min(...values) - 1) : 0;
    const hi = values.length ? Math.ceil(Math.max(...values) + 1) : 1;
    const spans = (run?.steps || [])
      .map((s) => [Date.parse(s.pump_started_at), Date.parse(s.pump_stopped_at)])
      .filter(([a, b]) => Number.isFinite(a) && Number.isFinite(b) && b > a);
    return {
      rows: all,
      domain: [lo, hi],
      pumping: spans,
      has: { spectrum: spectrum.length > 0, jumps: jumps.length > 0, ruler: ruler.length > 0 },
    };
  }, [readings, run]);
  if (!rows.length)
    return <p className="field-caption">No level to draw yet: the node's readings appear here as they arrive.</p>;
  const low = finiteNumber(limits?.tank_min_cm);
  const high = finiteNumber(limits?.tank_max_cm);
  const near = (v) => v !== null && v >= domain[0] && v <= domain[1];
  const ceiling = finiteNumber(limits?.tank_ceiling_cm);
  const start = finiteNumber(run?.start_level_cm);
  const drain = finiteNumber(limits?.max_drain_cm);
  const deepest = start !== null && drain !== null ? Math.round((start - drain) * 100) / 100 : null;
  const showCeiling = near(ceiling);
  const showDeepest = near(deepest);
  return (
    <figure style={{ margin: 0 }}>
      <div className="field-lab-chart">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={rows} margin={{ top: 10, right: 14, bottom: 0, left: 0 }}>
            <CartesianGrid stroke="#e8eff1" vertical={false} />
            {pumping.map(([a, b]) => (
              <ReferenceArea key={a} x1={a} x2={b} fill="#e3f1fb" fillOpacity={1} ifOverflow="hidden" />
            ))}
            <XAxis
              dataKey="ms"
              type="number"
              domain={["dataMin", "dataMax"]}
              tickFormatter={clock}
              tick={{ fontSize: 10, fill: "#6d8490" }}
            />
            <YAxis
              domain={domain}
              tickFormatter={(v) => `${v}`}
              tick={{ fontSize: 10, fill: "#6d8490" }}
              unit=" cm"
              width={60}
            />
            {low !== null && <ReferenceLine y={low} stroke={BOUNDS_COLOR} strokeDasharray="4 3" ifOverflow="hidden" />}
            {high !== null && <ReferenceLine y={high} stroke={BOUNDS_COLOR} strokeDasharray="4 3" ifOverflow="hidden" />}
            {showCeiling && (
              <ReferenceLine y={ceiling} stroke={CEILING_COLOR} strokeWidth={1.5} strokeDasharray="7 3" ifOverflow="hidden" />
            )}
            {showDeepest && (
              <ReferenceLine y={deepest} stroke={BOUNDS_COLOR} strokeWidth={1.5} strokeDasharray="1 3" ifOverflow="hidden" />
            )}
            <Tooltip content={<Tip />} />
            <Line
              dataKey="planned"
              name="Planned level"
              stroke={LIVE_COLORS.radar}
              strokeWidth={1.5}
              strokeDasharray="5 4"
              dot={false}
              connectNulls
              isAnimationActive={false}
            />
            {has.spectrum && (
              <Line
                dataKey="spectrum"
                name="Spectrum"
                stroke={SPECTRUM_COLOR}
                strokeWidth={1}
                strokeDasharray="2 3"
                dot={false}
                connectNulls
                isAnimationActive={false}
              />
            )}
            <Line
              dataKey="measured"
              name="Measured level"
              stroke={LIVE_COLORS.ours}
              strokeWidth={2}
              dot={{ r: 1.5, fill: LIVE_COLORS.ours, stroke: "none" }}
              connectNulls
              isAnimationActive={false}
            />
            {has.jumps && (
              <Line
                dataKey="jump"
                name="Jumped"
                stroke="none"
                dot={{ r: 4, fill: "white", stroke: JUMP_COLOR, strokeWidth: 2 }}
                activeDot={false}
                isAnimationActive={false}
              />
            )}
            {has.ruler && (
              <Line
                dataKey="ruler"
                name="Ruler camera"
                stroke="none"
                dot={{ r: 5, fill: RULER_COLOR, stroke: "white", strokeWidth: 1.5 }}
                activeDot={false}
                isAnimationActive={false}
              />
            )}
          </ComposedChart>
        </ResponsiveContainer>
      </div>
      <figcaption>
        <ul className="field-radar-legend">
          <Key color={LIVE_COLORS.ours}>Measured (node 1)</Key>
          {has.spectrum && (
            <Key kind="dotted" color={SPECTRUM_COLOR}>
              Spectrum alone
            </Key>
          )}
          <Key kind="dashed" color={LIVE_COLORS.radar}>
            Planned
          </Key>
          {has.ruler && (
            <Key kind="dot" color={RULER_COLOR}>
              Ruler camera (step photos)
            </Key>
          )}
          {has.jumps && (
            <Key kind="ring" color={JUMP_COLOR}>
              Jumped faster than the pump
            </Key>
          )}
          <Key kind="area" color="#e3f1fb">
            The pump running
          </Key>
          {(low !== null || high !== null) && (
            <Key kind="dotted" color={BOUNDS_COLOR}>
              Tank bounds
            </Key>
          )}
          {showCeiling && (
            <Key kind="dashed" color={CEILING_COLOR}>
              Tank ceiling, {formatCm(ceiling, 2)}
            </Key>
          )}
          {showDeepest && (
            <Key kind="dotted" color={BOUNDS_COLOR}>
              Deepest drain allowed, {formatCm(deepest, 2)} ({formatCm(drain)} below the start)
            </Key>
          )}
        </ul>
      </figcaption>
    </figure>
  );
});

export default LabLevelChart;
